import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';

/* ---------- Types ---------- */

export interface ShowcaseScreen {
  id: string;
  screenType: string;
  sortOrder: number;
  dwellTimeSeconds: number;
  config: Record<string, unknown> | null;
  enabled: boolean;
}

export interface ShowcaseOrganization {
  name: string;
  primaryColor: string | null;
  logoUrl: string | null;
  secondaryColor: string | null;
  backgroundImageUrl: string | null;
  fontFamily: string | null;
}

export interface CircuitStat {
  key: string;
  value: string;
  unit?: string;
}

export interface ShowcaseCircuit {
  name: string;
  city: string | null;
  countryCode: string | null;
  timezone: string | null;
  latitude: number | null;
  longitude: number | null;
  layout: number[][] | null;
  layoutSvg: string | null;
  stats: CircuitStat[] | null;
}

export interface ShowcaseEvent {
  name: string;
  startDate: string | null;
  endDate: string | null;
  timezone: string | null;
  circuit: ShowcaseCircuit | null;
}

export interface ShowcaseData {
  id: string;
  name: string;
  token: string;
  isActive: boolean;
  loop: boolean;
  transitionType: 'fade' | 'slide' | 'none';
  transitionDurationMs: number;
  screens: ShowcaseScreen[];
  organization: ShowcaseOrganization | null;
  event: ShowcaseEvent | null;
  themeOverrides: {
    primaryColor?: string;
    logoUrl?: string;
    backgroundUrl?: string;
  } | null;
}

interface UseShowcaseDataReturn {
  showcase: ShowcaseData | null;
  isLoading: boolean;
  error: string | null;
}

/* ---------- Helpers ---------- */

/** Cache key prefix for localStorage showcase data. */
const CACHE_KEY_PREFIX = 'sc-cache-';

/** Save raw showcase RPC response to localStorage for offline fallback. */
function trySaveToCache(token: string, data: Record<string, unknown>): void {
  try {
    localStorage.setItem(`${CACHE_KEY_PREFIX}${token}`, JSON.stringify(data));
  } catch {
    // localStorage full or unavailable — non-blocking
  }
}

/** Load cached showcase data from localStorage. Returns null if not found. */
function tryLoadFromCache(token: string): ShowcaseData | null {
  try {
    const raw = localStorage.getItem(`${CACHE_KEY_PREFIX}${token}`);
    if (!raw) return null;
    return toShowcaseData(JSON.parse(raw));
  } catch {
    return null;
  }
}

function toShowcaseData(raw: Record<string, unknown>): ShowcaseData {
  const rawScreens = (raw.screens as Record<string, unknown>[]) || [];
  const screens: ShowcaseScreen[] = rawScreens
    .map((s) => {
      // Merge RPC-resolved data into config so screen components can access it
      const baseConfig = (s.config as Record<string, unknown>) ?? {};
      const resolvedDriver = s._resolved_driver ?? null;
      const resolvedCar = s._resolved_car ?? null;
      const resolvedSponsors = s._resolved_sponsors ?? null;

      const config: Record<string, unknown> = {
        ...baseConfig,
        ...(resolvedDriver ? { _resolved_driver: resolvedDriver } : {}),
        ...(resolvedCar ? { _resolved_car: resolvedCar } : {}),
        ...(resolvedSponsors ? { _resolved_sponsors: resolvedSponsors } : {}),
      };

      return {
        id: s.id as string,
        screenType: s.screen_type as string,
        sortOrder: (s.sort_order as number) ?? 0,
        dwellTimeSeconds: (s.dwell_time_seconds as number) ?? 15,
        config: Object.keys(config).length > 0 ? config : null,
        enabled: (s.enabled as boolean) ?? true,
      };
    })
    // Filter out disabled screens — they should not be displayed
    .filter((s) => s.enabled)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // Organization branding comes from the RPC which extracts from branding JSONB
  const rawOrg = raw.organization as Record<string, unknown> | null;
  const organization: ShowcaseOrganization | null = rawOrg
    ? {
        name: rawOrg.name as string,
        primaryColor: (rawOrg.primary_color as string) ?? null,
        logoUrl: (rawOrg.logo_url as string) ?? null,
        secondaryColor: (rawOrg.secondary_color as string) ?? null,
        backgroundImageUrl: (rawOrg.background_image_url as string) ?? null,
        fontFamily: (rawOrg.font_family as string) ?? null,
      }
    : null;

  const rawEvent = raw.event as Record<string, unknown> | null;
  let event: ShowcaseEvent | null = null;
  if (rawEvent) {
    const rawCircuit = rawEvent.circuit as Record<string, unknown> | null;
    event = {
      name: rawEvent.name as string,
      startDate: (rawEvent.start_date as string) ?? null,
      endDate: (rawEvent.end_date as string) ?? null,
      timezone: (rawEvent.timezone as string) ?? null,
      circuit: rawCircuit
        ? {
            name: rawCircuit.name as string,
            city: (rawCircuit.city as string) ?? null,
            countryCode: (rawCircuit.country_code as string) ?? null,
            timezone: (rawCircuit.timezone as string) ?? null,
            latitude: (rawCircuit.latitude as number) ?? null,
            longitude: (rawCircuit.longitude as number) ?? null,
            layout: (rawCircuit.layout as number[][]) ?? null,
            layoutSvg: (rawCircuit.layout_svg as string) ?? null,
            stats: (rawCircuit.stats as CircuitStat[]) ?? null,
          }
        : null,
    };
  }

  // Theme overrides come from separate DB columns (not a JSONB object)
  const themePrimary = raw.theme_primary_color as string | null;
  const themeLogo = raw.theme_logo_url as string | null;
  const themeBg = raw.theme_background_url as string | null;
  const themeOverrides =
    themePrimary || themeLogo || themeBg
      ? {
          primaryColor: themePrimary ?? undefined,
          logoUrl: themeLogo ?? undefined,
          backgroundUrl: themeBg ?? undefined,
        }
      : null;

  return {
    id: raw.id as string,
    name: raw.name as string,
    token: raw.token as string,
    isActive: raw.is_active as boolean,
    loop: (raw.loop as boolean) ?? true,
    transitionType: (raw.transition_type as ShowcaseData['transitionType']) ?? 'fade',
    transitionDurationMs: (raw.transition_duration_ms as number) ?? 800,
    screens,
    organization,
    event,
    themeOverrides,
  };
}

/* ---------- Hook ---------- */

/** Polling interval for live config updates (5 minutes) */
const POLL_INTERVAL_MS = 5 * 60 * 1000;

export function useShowcaseData(token: string | undefined): UseShowcaseDataReturn {
  const [showcase, setShowcase] = useState<ShowcaseData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchShowcase = useCallback(async (isInitial = false) => {
    if (!token) {
      setError('No showcase token provided.');
      setIsLoading(false);
      return;
    }

    try {
      if (isInitial) {
        setIsLoading(true);
        setError(null);
      }

      // Use the SECURITY DEFINER RPC — no direct table access needed
      const { data, error: rpcError } = await supabase
        .rpc('get_showcase_by_token', { lookup_token: token });

      if (rpcError) {
        // Network/RPC failure — try localStorage fallback
        const cached = tryLoadFromCache(token);
        if (cached && isInitial) {
          setShowcase(cached);
          setError(null);
          setIsLoading(false);
          return;
        }
        setError(`Failed to load showcase: ${rpcError.message}`);
        if (isInitial) setIsLoading(false);
        return;
      }

      if (!data) {
        setError('This showcase is not available or has been deactivated.');
        // If we had data before and now it's null, the showcase was deactivated
        if (!isInitial && showcase) {
          setShowcase(null);
        }
        if (isInitial) setIsLoading(false);
        return;
      }

      const showcaseData = toShowcaseData(data as Record<string, unknown>);
      setShowcase(showcaseData);
      setError(null);
      if (isInitial) setIsLoading(false);

      // Cache for offline resilience — TV can play even after losing internet
      trySaveToCache(token, data as Record<string, unknown>);
    } catch (err) {
      // Network completely unavailable — try localStorage fallback
      const cached = tryLoadFromCache(token);
      if (cached && isInitial) {
        setShowcase(cached);
        setError(null);
        setIsLoading(false);
        return;
      }
      setError(
        `Unexpected error: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
      if (isInitial) setIsLoading(false);
    }
  }, [token, showcase]);

  // Initial fetch
  useEffect(() => {
    fetchShowcase(true);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for config changes (so admins editing the showcase are reflected without TV refresh)
  useEffect(() => {
    if (!token) return;

    pollRef.current = setInterval(() => {
      fetchShowcase(false);
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  return { showcase, isLoading, error };
}
